import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { AiOutlineCloudDownload } from "react-icons/ai";
import { BiBarcodeReader } from "react-icons/bi";
import { ImCopy } from "react-icons/im";
import { IoCloseOutline } from "react-icons/io5";
import { SiPix } from "react-icons/si";
import { toast } from "react-toastify";
import { NegociacaoBoletoUnico } from "src/@types/boletoUnico";

interface Props {
  open: boolean;
  onClose: () => void;
  negociacao: NegociacaoBoletoUnico;
}

const moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export default function BoletoUnicoModal({ open, onClose, negociacao }: Props) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const pix = negociacao.qrcodePix?.trim() ?? "";
  const linhaDigitavel = negociacao.linhaDigitavel?.trim() ?? "";
  const linkBoleto = negociacao.linkBoleto?.trim() ?? "";

  const temPix = pix.length > 0;
  const temBoleto = linhaDigitavel.length > 0;

  const [tab, setTab] = useState<"pix" | "boleto">(temPix ? "pix" : "boleto");
  const currentTab = temPix && temBoleto ? tab : temPix ? "pix" : "boleto";

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "boleto-unico-copiado" : undefined;

  const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const visualizarBoleto = () => {
    const janela = window.open(linkBoleto, "_blank");
    if (!janela || janela.closed) {
      toast.warning("O bloqueador de popup está ativo. Libere os popups para abrir o boleto.", {
        position: toast.POSITION.TOP_RIGHT,
        theme: "colored",
      });
    }
  };

  const CodigoBox = ({ codigo }: { codigo: string }) => (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[100],
        borderRadius: 1,
        padding: 2,
        marginTop: currentTab === "pix" ? 3 : 0,
        width: "100%",
      }}
    >
      <Typography
        variant="body2"
        color={theme.palette.grey[800]}
        textAlign="center"
        sx={{ wordBreak: "break-all" }}
      >
        {codigo}
      </Typography>
    </Box>
  );

  const BotaoCopiar = ({ codigo }: { codigo: string }) => (
    <CopyToClipboard text={codigo}>
      <Button
        aria-describedby={id}
        onClick={handleClickPopover}
        variant="contained"
        color="secondary"
        fullWidth
        startIcon={<ImCopy style={{ width: 18, height: 18 }} />}
      >
        Copiar código
      </Button>
    </CopyToClipboard>
  );

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <DialogContent sx={{ padding: "0px" }}>
        <Box
          sx={{
            padding: "20px 20px 24px 20px",
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography>Pagar Fatura</Typography>
          <IoCloseOutline size={24} onClick={onClose} style={{ cursor: "pointer" }} />
        </Box>

        {/* ------------------- Resumo do boleto ------------------- */}
        <Box sx={{ backgroundColor: theme.palette.background.neutral, padding: "16px 20px" }}>
          <Stack
            textAlign="center"
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 4, md: 2 }}
            justifyContent="space-around"
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight="400"
                color={theme.palette.grey[800]}
                fontSize="14px"
              >
                Vencimento
              </Typography>
              <Typography
                variant="body2"
                fontWeight="700"
                color={theme.palette.grey[800]}
                fontSize="14px"
              >
                {negociacao.vencimento ? moment(negociacao.vencimento).format("DD/MM/YYYY") : "--"}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight="400"
                color={theme.palette.grey[800]}
                fontSize="14px"
              >
                Valor
              </Typography>
              <Typography
                variant="body2"
                fontWeight="700"
                color={theme.palette.secondary.main}
                fontSize="14px"
              >
                {moeda.format(negociacao.valor)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ------------------- Abas PIX / Boleto ------------------- */}
        {temPix && temBoleto && (
          <Tabs
            value={tab}
            onChange={(_, value) => {
              setTab(value);
              setAnchorEl(null);
            }}
            variant="fullWidth"
            indicatorColor="secondary"
            textColor="secondary"
            sx={{ borderBottom: "1px solid rgba(145, 158, 171, 0.24)" }}
          >
            <Tab
              value="pix"
              label="PIX"
              icon={<SiPix style={{ height: 20, width: 20 }} />}
              iconPosition="start"
              sx={{ "&.Mui-selected": { color: theme.palette.secondary.main } }}
            />
            <Tab
              value="boleto"
              label="Boleto"
              icon={<BiBarcodeReader style={{ height: 20, width: 20 }} />}
              iconPosition="start"
              sx={{ "&.Mui-selected": { color: theme.palette.secondary.main } }}
            />
          </Tabs>
        )}

        {/* ------------------- Conteúdo da aba ------------------- */}
        <Box
          sx={{
            padding: "24px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {currentTab === "pix" ? (
            <>
              <Typography
                variant="h6"
                color={theme.palette.secondary.main}
                textAlign="center"
                gutterBottom
              >
                Pagamento via PIX
              </Typography>
              <Typography variant="body2" textAlign="center">
                Copie ou escaneie o QR Code.
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                textAlign="center"
                sx={{ mb: 3 }}
              >
                Ao copiar o código, abra o aplicativo do seu banco e realize o pagamento de forma
                rápida.
              </Typography>

              <QRCodeSVG
                value={pix}
                size={200}
                bgColor={"#ffffff"}
                fgColor={theme.palette.grey[900]}
                level={"L"}
                includeMargin={false}
              />
              <CodigoBox codigo={pix} />

              <Stack
                spacing={1.5}
                sx={{ width: "100%", marginTop: 3, "& .MuiButton-root": { py: 1.5 } }}
              >
                <BotaoCopiar codigo={pix} />
              </Stack>
            </>
          ) : (
            <>
              <Typography
                variant="h6"
                color={theme.palette.secondary.main}
                textAlign="center"
                gutterBottom
              >
                Pagamento via boleto
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                textAlign="center"
                sx={{ mb: 3 }}
              >
                Copie a linha digitável abaixo para pagar no app do seu banco.
              </Typography>

              {temBoleto && <CodigoBox codigo={linhaDigitavel} />}

              <Stack
                spacing={1.5}
                sx={{ width: "100%", marginTop: 3, "& .MuiButton-root": { py: 1.4 } }}
              >
                {temBoleto && <BotaoCopiar codigo={linhaDigitavel} />}
                {linkBoleto.length > 0 && (
                  <Button
                    onClick={visualizarBoleto}
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    startIcon={<AiOutlineCloudDownload style={{ width: 18, height: 18 }} />}
                  >
                    Visualizar boleto
                  </Button>
                )}
              </Stack>
            </>
          )}

          <Popover
            id={id}
            open={openPopover}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            style={{ marginLeft: 5 }}
          >
            <Typography sx={{ p: 2 }}>Código copiado!</Typography>
          </Popover>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

